import getFromCdn from '@anm/helpers/getFromCdn';
import compose from 'lodash/fp/compose';
import keys from 'lodash/fp/keys';
import pickBy from 'lodash/fp/pickBy';
import { FC } from 'react';

import Badge from '../../Badge';
import useDirection from './hooks/useDirection';

import TooltipWrapper, {
  embeddedTooltip,
  hostedVideoDraftTooltip,
  landingTooltip,
  permissionTooltip,
  unHostedVideoDraftTooltip
} from './tooltips';
import { PlayerStatusesProps, Status, StatusesParams, StatusData } from './types';

export const DRAFT = 'isDraft';
export const EMBEDDED = 'isEmbedded';
export const PROTECTED = 'isProtected';
export const LANDING = 'isLanding';
export type Statuses = { [key in Status]?: boolean };

const getStatusesData = ({
  hostedId,
  renderEditLink,
  renderEmbedLink,
  renderLandingLink,
  renderPermissionLink
}: StatusesParams): { [key in Status]: StatusData } => ({
  [DRAFT]: {
    title: 'Draft',
    icon: getFromCdn('images/dashboard/draft-thin-icon.svg'),
    badgeVariant: 'orange',
    renderTooltip: hostedId
      ? () => hostedVideoDraftTooltip({ renderLink: renderEditLink })
      : () => unHostedVideoDraftTooltip({ renderLink: renderEditLink })
  },
  [EMBEDDED]: {
    icon: getFromCdn('images/dashboard/embed-icon2.svg'),
    title: 'Embed',
    badgeVariant: 'blue',
    renderTooltip: () => embeddedTooltip({ renderLink: renderEmbedLink }),
    tooltipDirection: 'to-bottom'
  },
  [PROTECTED]: {
    icon: getFromCdn('images/dashboard/password-icon2.svg'),
    title: 'Password',
    badgeVariant: 'orange',
    renderTooltip: () => permissionTooltip({ renderLink: renderPermissionLink }),
    tooltipDirection: 'to-bottom'
  },
  [LANDING]: {
    icon: getFromCdn('images/dashboard/landing-icon2.svg'),
    title: 'Page',
    badgeVariant: 'green',
    renderTooltip: () => landingTooltip({ renderLink: renderLandingLink }),
    tooltipDirection: 'to-bottom'
  }
});

const PlayerStatuses: FC<PlayerStatusesProps> = ({ hostedId, statuses, ...renderLinksProps }) => {
  const list: Status[] = compose<any, any, any>(keys, pickBy(Boolean))(statuses);
  const [draftDirection, hoverRef] = useDirection();

  return (
    <>
      {list.map(status => {
        const { icon, title, badgeVariant, renderTooltip, tooltipDirection } = getStatusesData({
          hostedId,
          ...renderLinksProps
        })[status];

        const isDraft = status === 'isDraft';

        return (
          <TooltipWrapper
            key={title}
            tooltipTitle={title}
            variant="black"
            delayShow={500}
            delayHide={500}
            width="fit-content"
            direction={isDraft ? draftDirection : tooltipDirection}
            renderContent={renderTooltip}
            withArrow
          >
            <Badge icon={icon} variant={badgeVariant} badgeRef={hoverRef}>
              {title}
            </Badge>
          </TooltipWrapper>
        );
      })}
    </>
  );
};

export default PlayerStatuses;
